import { Headset, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePageTranslation } from "@/i18n/useTranslation";
import { supportTranslations as enSupport } from "@/i18n/locales/en/support";
import { supportTranslations as thSupport } from "@/i18n/locales/th/support";

/**
 * Support team presence header — Intercom-style: a few teammate avatars,
 * "we're online" with a small status dot, and a plain reply-time line. Calm and
 * human; no coloured card, no fake fluctuating metrics.
 */
const AVATARS = [
  { Icon: Headset, className: "bg-primary text-white" },
  { Icon: User, className: "bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-300" },
  { Icon: User, className: "bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-300" },
];

export function SupportPresence({ className }: { className?: string }) {
  const t = usePageTranslation({ en: enSupport, th: thSupport }) as typeof enSupport;

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2.5 dark:border-gray-800 dark:bg-gray-900/40",
        className,
      )}
    >
      <div className="flex -space-x-2 shrink-0">
        {AVATARS.map(({ Icon, className: avatarClass }, i) => (
          <div
            key={i}
            className={cn(
              "flex h-7 w-7 items-center justify-center rounded-full ring-2 ring-white dark:ring-gray-900",
              avatarClass,
            )}
          >
            <Icon className="h-3.5 w-3.5" />
          </div>
        ))}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {t.presenceOnline}
          </span>
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
        </div>
        <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
          {t.presenceReplyTime}
        </p>
      </div>
    </div>
  );
}
